import React, { useContext } from "react";
import { Box, Paper, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DataContext } from "../../../context/DataContext";
import { getRuta, isAccess } from "../../../helpers/Helpers";
import { EstadoError, KpiCard } from "../../../components/base";

// `isAccess` revienta si el permiso no viene en la lista (p. ej. antes de que llegue el perfil).
// Aquí un permiso que no se puede comprobar equivale a "no puede": el atajo se oculta.
const tienePermiso = (access, permiso) => {
  try {
    return isAccess(access, permiso) === true;
  } catch (e) {
    return false;
  }
};

/**
 * Indicador del panel: `KpiCard` con su esqueleto, su error y su atajo.
 *
 * @param {object} props
 * @param {string} props.titulo
 * @param {number|string} props.valor
 * @param {"monto"|"entero"} [props.formato]
 * @param {React.ReactNode} props.icono
 * @param {"primary"|"success"|"warning"|"error"|"info"} props.tono
 * @param {boolean} props.cargando Mientras carga: esqueleto, nunca "0" ni "C$0.00".
 * @param {boolean|"conexion"} props.error Si falló: `EstadoError` dentro de la tarjeta.
 * @param {Function} props.onReintentar
 * @param {string} [props.destino] Ruta existente (sin el prefijo de la app) a la que lleva el atajo.
 * @param {string} [props.permiso] Permiso con el que el menú muestra esa sección; sin él, no hay atajo.
 * @param {string} [props.ayuda] Texto de ayuda del atajo.
 * @param {React.ReactNode} [props.detalle] Línea de contexto bajo la cifra.
 * @param {boolean} [props.anunciarError] false si otra tarjeta ya anuncia el mismo fallo.
 */
const KpiPanel = ({
  titulo,
  valor,
  formato,
  icono,
  tono,
  cargando,
  error,
  onReintentar,
  destino,
  permiso,
  ayuda,
  detalle,
  anunciarError = true,
}) => {
  const { access } = useContext(DataContext);
  const navigate = useNavigate();
  const ruta = getRuta();

  if (error) {
    // Tarjeta compacta: el EstadoError de pantalla completa sería mucho más alto que un KPI.
    return (
      <Paper sx={{ height: "100%" }}>
        <Box
          sx={{
            "& > div": { py: 2 },
            "& > div > svg": { fontSize: 36 },
          }}
        >
          <EstadoError
            titulo={titulo}
            mensaje={
              error === "conexion"
                ? "No se pudo conectar con el servidor."
                : "No se pudo cargar."
            }
            onReintentar={onReintentar}
            anunciar={anunciarError}
          />
        </Box>
      </Paper>
    );
  }

  const puedeIr = Boolean(destino) && tienePermiso(access, permiso);

  const tarjeta = (
    <KpiCard
      titulo={titulo}
      valor={valor}
      formato={formato}
      detalle={detalle}
      icono={icono}
      tono={tono}
      cargando={cargando}
      onClick={puedeIr ? () => navigate(`${ruta}${destino}`) : undefined}
    />
  );

  return puedeIr && ayuda ? (
    <Tooltip title={ayuda} describeChild>
      <Box sx={{ height: "100%" }}>{tarjeta}</Box>
    </Tooltip>
  ) : (
    tarjeta
  );
};

export default KpiPanel;
